import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import PriceComparisonAlertExecutionNotification from '@app/model/alert/PriceComparisonAlertExecutionNotification';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';
import Tag from '@app/component/tag/Tag';
import AlertUtilities from '@app/site/secured/rateshopper/rates/alert/util/AlertUtilities';
import CompetitorsPriceRateAlertExecutionNotification
	from '@app/model/alert/CompetitorsPriceRateAlertExecutionNotification';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import MarketStateService from '@app/model/market_state/service/MarketStateService';
import { MarketStateNotificationsQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import {
	useFindMarketStateNotificationsForStatDate,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useFindMarketStateNotificationsForStatDate';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import CalendarDay from '@app/state/calendar/CalendarDay';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import ParityPriceComparisonAlertExecutionNotification
	from '@app/model/alert/ParityPriceComparisonAlertExecutionNotification';
import CompetitorsSoldOutAlertExecutionNotification
	from '@app/model/alert/CompetitorsSoldOutAlertExecutionNotification';

const MarketStateSnapshotDailyAlertNotificationsFragment: React.FunctionComponent<{
	calendarDay: CalendarDay,
}> = ({ calendarDay }) => {

	const queryClient = useQueryClient();
	const marketStateForDayNotifications = useFindMarketStateNotificationsForStatDate(calendarDay.display);
	const { companyHotel } = useSelectedCompanyHotel()!;
	const [isExpanded, setIsExpanded] = useState(false);

	const markAlertExecutionNotificationAsRead = useMutation({
		mutationFn: MarketStateService.markAlertExecutionNotificationAsRead,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: MarketStateNotificationsQueryKeys.marketStateNotifications(companyHotel.id, calendarDay.index.year, calendarDay.index.month) });
		},
	});

	const onNotificationDelete = (notificationId: number) => {
		markAlertExecutionNotificationAsRead.mutate({ companyHotelId: companyHotel.id, notificationId });
	};

	return !!marketStateForDayNotifications && marketStateForDayNotifications.hasNotifications ? <div className="mt-4">
		<div className="rounded-md shadow shadow-yellow-900/20 bg-yellow-50 pt-4 pb-3 px-6">
			<div className="flex justify-between items-center">
				<div className="flex justify-start items-start ">
					<div className="flex-shrink-0">
						<ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
					</div>
					<div className="ml-2">
						<h3 className="text-sm font-medium text-yellow-800">Attention needed for your below alerts!</h3>
					</div>
				</div>
				<button type="button" onClick={() => setIsExpanded(!isExpanded)}
				        className="rounded-md bg-yellow-50 text-yellow-700 hover:bg-yellow-100 hover:text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2">
					{isExpanded ? <ChevronUpIcon className="h-6 w-6" /> : <ChevronDownIcon className="h-6 w-6" />}
				</button>
			</div>

			<div className={`mt-2 ${isExpanded ? 'block' : 'hidden'}`}>
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-yellow-400">
							<thead>
							<tr>
								<th scope="col"
								    className="py-2 px-3 text-left text-xs font-semibold text-yellow-900 sm:pl-0">
									Alert Name
								</th>
								<th scope="col"
								    className="py-2 px-3 text-left text-xs font-semibold text-yellow-900">Type
								</th>
								<th scope="col" className="py-2 px-3"></th>
								<th scope="col" className="py-2 px-3"></th>
							</tr>
							</thead>
							<tbody className="divide-y divide-yellow-300">
							{marketStateForDayNotifications.priceComparisonAlertExecutionNotification &&
								<PriceComparisonAlertExecutionNotificationNotificationRow
									n={marketStateForDayNotifications.priceComparisonAlertExecutionNotification}
									statDate={calendarDay.display}
									onNotificationDelete={onNotificationDelete} />}
							{marketStateForDayNotifications.competitorsPriceRateAlertExecutionNotification &&
								<CompetitorsPriceRateAlertExecutionNotificationNotificationRow
									n={marketStateForDayNotifications.competitorsPriceRateAlertExecutionNotification}
									statDate={calendarDay.display}
									onNotificationDelete={onNotificationDelete} />}
							{marketStateForDayNotifications.parityPriceComparisonAlertExecutionNotification &&
								<ParityPriceComparisonAlertExecutionNotificationNotificationRow
									n={marketStateForDayNotifications.parityPriceComparisonAlertExecutionNotification}
									statDate={calendarDay.display}
									onNotificationDelete={onNotificationDelete} />}
							{marketStateForDayNotifications.competitorsSoldOutAlertExecutionNotification &&
								<CompetitorsSoldOutAlertExecutionNotificationNotificationRow
									n={marketStateForDayNotifications.competitorsSoldOutAlertExecutionNotification}
									statDate={calendarDay.display}
									onNotificationDelete={onNotificationDelete} />}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div> : <div />;
};

const PriceComparisonAlertExecutionNotificationNotificationRow: React.FunctionComponent<{
	n: PriceComparisonAlertExecutionNotification,
	statDate: string,
	onNotificationDelete: (alertExecutionId: number) => any
}> = ({ n, statDate, onNotificationDelete }) => {
	const tools = useMarketStateTools();

	n.difference = AlertUtilities.calculateDifference(n);
	return <tr>
		<td className="whitespace-nowrap px-3 py-2 text-xs font-medium text-yellow-700 sm:pl-0">
			{n.alert.name}
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			Price Change
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			<div className="inline-flex items-center gap-x-2">
				<span>You</span>
				<span>{Utilities.formatPrice(n.price)}</span>
				<span>•</span>
				<span>
						{n.alertRule.pricePoint === PriceComparisonAlertRulePricePoint.MEDIAN && 'Median'}
					{n.alertRule.pricePoint === PriceComparisonAlertRulePricePoint.MIN && 'Minimum'}
					{n.alertRule.pricePoint === PriceComparisonAlertRulePricePoint.MAX && 'Maximum'}
				</span>
				<span>•</span>
				<span>
          {n.difference! <= 0 && <Tag state="success">{Utilities.formatDouble(n.difference, 0)}%</Tag>}
					{n.difference! > 0 && <Tag state="critical">+{Utilities.formatDouble(n.difference, 0)}%</Tag>}
				</span>
			</div>
		</td>
		<td className="relative whitespace-nowrap px-3 py-2 text-right text-xs font-medium sm:pr-0">
			<a onClick={() => tools.openNotificationsDrawer(n.alertExecutionId, statDate)}
			   className="text-xs font-medium text-yellow-800 hover:text-yellow-900 cursor-pointer">Open</a>
			<span className="text-xs font-medium text-yellow-800 hover:text-yellow-900">&nbsp;|&nbsp;</span>
			<a onClick={() => onNotificationDelete(n.id)}
			   className="text-xs font-medium text-orange-800 hover:text-orange-900 cursor-pointer">Dismiss</a>
		</td>
	</tr>;
};

const ParityPriceComparisonAlertExecutionNotificationNotificationRow: React.FunctionComponent<{
	n: ParityPriceComparisonAlertExecutionNotification,
	statDate: string,
	onNotificationDelete: (alertExecutionId: number) => any
}> = ({ n, statDate, onNotificationDelete }) => {

	const tools = useMarketStateTools();

	const avgDifference = n.providerStats.length
		? n.providerStats.reduce((sum, p) => sum + p.difference, 0) / n.providerStats.length
		: 0;
	return <tr>
		<td className="whitespace-nowrap px-3 py-2 text-xs font-medium text-yellow-700 sm:pl-0">
			{n.alert.name}
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			Parity Price Comparison
		</td>
		{/* Prices + difference */}
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			<div className="inline-flex items-center gap-x-2">
				{/* Your (brand) price */}
				<span>You</span>
				<span className="font-semibold">{Utilities.formatPrice(n.brandPrice)}</span>

				<span>•</span>
				Avg Providers Amount
				<span>•</span>
				<span className="font-semibold">{Utilities.formatAmount(avgDifference, n.brandPrice.currency)}</span>

			</div>
		</td>
		<td className="relative whitespace-nowrap px-3 py-2 text-right text-xs font-medium sm:pr-0">
			<a onClick={() => tools.openNotificationsDrawer(n.alertExecutionId, statDate)}
			   className="text-xs font-medium text-yellow-800 hover:text-yellow-900 cursor-pointer">Open</a>
			<span className="text-xs font-medium text-yellow-800 hover:text-yellow-900">&nbsp;|&nbsp;</span>
			<a onClick={() => onNotificationDelete(n.id)}
			   className="text-xs font-medium text-orange-800 hover:text-orange-900 cursor-pointer">Dismiss</a>
		</td>
	</tr>;
};

const CompetitorsPriceRateAlertExecutionNotificationNotificationRow: React.FunctionComponent<{
	n: CompetitorsPriceRateAlertExecutionNotification,
	statDate: string,
	onNotificationDelete: (alertExecutionId: number) => any
}> = ({ n, statDate, onNotificationDelete }) => {
	const tools = useMarketStateTools();

	return <tr>
		<td className="whitespace-nowrap px-3 py-2 text-xs font-medium text-yellow-700 sm:pl-0">
			{n.alert.name}
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			Competition Rate Change
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			<div className="inline-flex items-center gap-x-2">
				<div className="inline-flex items-center gap-x-2">
					<span>Average Change</span>
					<span>
            {n.average < 0 && <Tag state="critical">{Utilities.formatDouble(n.average * 100, 0)}%</Tag>}
						{n.average >= 0 && <Tag state="success">+{Utilities.formatDouble(n.average * 100, 0)}%</Tag>}
					</span>
				</div>
			</div>
		</td>
		<td className="relative whitespace-nowrap px-3 py-2 text-right text-xs font-medium sm:pr-0">
			<a onClick={() => tools.openNotificationsDrawer(n.alertExecutionId, statDate)}
			   className="text-xs font-medium text-yellow-800 hover:text-yellow-900 cursor-pointer">Open</a>
			<span className="text-xs font-medium text-yellow-800 hover:text-yellow-900">&nbsp;|&nbsp;</span>
			<a onClick={() => onNotificationDelete(n.id)}
			   className="text-xs font-medium text-orange-800 hover:text-orange-900 cursor-pointer">Dismiss</a>
		</td>
	</tr>;
};

const CompetitorsSoldOutAlertExecutionNotificationNotificationRow: React.FunctionComponent<{
	n: CompetitorsSoldOutAlertExecutionNotification,
	statDate: string,
	onNotificationDelete: (alertExecutionId: number) => any
}> = ({ n, statDate, onNotificationDelete }) => {
	const tools = useMarketStateTools();

	return <tr>
		<td className="whitespace-nowrap px-3 py-2 text-xs font-medium text-yellow-700 sm:pl-0">
			{n.alert.name}
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			Sold Out Competitors
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			{n.soldOutHotelIds.map(hId => tools.companyHotel.competitors?.find(h => h.id === hId)).filter(hotel => !!hotel).map(hotel => hotel!.name).join(', ')}
		</td>
		<td className="whitespace-nowrap px-3 py-2 text-xs text-yellow-700">
			<div className="inline-flex items-center gap-x-2">
				<div className="inline-flex items-center gap-x-2">
					<Tag state="critical">Sold Out</Tag>
				</div>
			</div>
		</td>
		<td className="relative whitespace-nowrap px-3 py-2 text-right text-xs font-medium sm:pr-0">
			<a onClick={() => tools.openNotificationsDrawer(n.alertExecutionId, statDate)}
			   className="text-xs font-medium text-yellow-800 hover:text-yellow-900 cursor-pointer">Open</a>
			<span className="text-xs font-medium text-yellow-800 hover:text-yellow-900">&nbsp;|&nbsp;</span>
			<a onClick={() => onNotificationDelete(n.id)}
			   className="text-xs font-medium text-orange-800 hover:text-orange-900 cursor-pointer">Dismiss</a>
		</td>
	</tr>;
};

export default MarketStateSnapshotDailyAlertNotificationsFragment;